import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import RichTextEditor from "@/components/common/RichTextEditor";
import { Send, Sparkles, Wand2, Loader2, Users, X, Mail, CheckCircle2 } from "lucide-react";

const SEGMENTS = [
  { key: "ST_USER", label: "ST User (alle)" },
  { key: "ST_CREATOR", label: "Creator" },
  { key: "MP_BUYER", label: "MP Buyer" },
  { key: "MP_SELLER", label: "MP Seller" },
  { key: "STX_BUYER", label: "STX Buyer" },
  { key: "STX_SELLER", label: "STX Seller" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function NewsletterSection() {
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [segments, setSegments] = useState(new Set(["ST_USER"]));
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [aiError, setAiError] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [campaigns, setCampaigns] = useState([]);

  const plainText = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").trim();

  const toggleSeg = (k) => {
    setSegments((prev) => {
      const nx = new Set(prev);
      if (nx.has(k)) nx.delete(k); else nx.add(k);
      return nx;
    });
  };

  const loadCount = useCallback(async () => {
    const segs = Array.from(segments);
    if (segs.length === 0) { setCount(0); return; }
    setCountLoading(true);
    try {
      const { data } = await api.post("/admin/newsletter/audience-count", { segments: segs });
      setCount(data.count);
    } catch { setCount(null); }
    finally { setCountLoading(false); }
  }, [segments]);

  const loadCampaigns = useCallback(async () => {
    try { const { data } = await api.get("/admin/newsletter/campaigns"); setCampaigns(data.campaigns || []); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const runAi = async (action) => {
    setAiBusy(action); setAiError("");
    try {
      const ctx = plainText(bodyHtml);
      const { data } = await api.post("/ai-assist/text", {
        area: "SOCIAL",
        action,
        context: ctx || (subject.trim() ? `Newsletter-Thema: ${subject}` : "Freundlicher Newsletter an unsere Community"),
        field_type: "announcement",
        emoji: true,
      });
      if (data.text) {
        const html = data.text.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
        setBodyHtml(html);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAiError(typeof d === "string" ? d : (d?.message || "KI-Fehler"));
    } finally { setAiBusy(null); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !plainText(bodyHtml)) { setTestResult("Bitte Betreff und Text ausfüllen."); return; }
    setTestBusy(true); setTestResult("");
    try {
      const { data } = await api.post("/admin/newsletter/test", { subject: subject.trim(), body_html: bodyHtml });
      setTestResult(data.sent ? `Testmail an ${data.to} gesendet.` : `Versand fehlgeschlagen: ${data.reason || "unbekannt"}`);
    } catch (e) { setTestResult(formatError(e)); }
    finally { setTestBusy(false); }
  };

  const doSend = async () => {
    setSending(true); setSendError("");
    try {
      await api.post("/admin/newsletter/send", {
        subject: subject.trim(), body_html: bodyHtml, segments: Array.from(segments),
      });
      setShowConfirm(false);
      setSubject(""); setBodyHtml("");
      await loadCampaigns();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSendError(typeof d === "string" ? d : (d?.message || formatError(e)));
    } finally { setSending(false); }
  };

  const canSend = subject.trim() && plainText(bodyHtml) && segments.size > 0 && (count ?? 0) > 0;

  return (
    <div data-testid="newsletter-section" className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[#0A0A0A]">Newsletter</h1>
        <p className="text-xs text-[#666] mt-1">Sammelmail an ausgewählte Freischaltungskreise. Es werden nur Nutzer mit Newsletter-Zustimmung angeschrieben (DSGVO) – inkl. Abmeldelink.</p>
      </div>

      {/* Segmente */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#999] mb-2">Empfängerkreise</p>
        <div className="flex flex-wrap gap-2">
          {SEGMENTS.map((seg) => {
            const active = segments.has(seg.key);
            return (
              <button
                key={seg.key}
                data-testid={`newsletter-seg-${seg.key}`}
                onClick={() => toggleSeg(seg.key)}
                className={`h-9 px-3 rounded-full text-xs font-medium transition border ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
              >
                {seg.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-[#0A0A0A]" data-testid="newsletter-audience">
          <Users size={15} className="text-[#666]" />
          {countLoading ? <Loader2 size={14} className="animate-spin" /> : (
            <span><strong>{count ?? "—"}</strong> Empfänger mit Newsletter-Zustimmung</span>
          )}
        </div>
      </div>

      {/* Betreff */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#999]">Betreff</label>
        <input
          data-testid="newsletter-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="z. B. Neuigkeiten von stilltouched"
          className="mt-1 w-full h-11 px-3 text-sm bg-white border border-[#E5E5E5] rounded focus:outline-none focus:border-[#0A0A0A]"
        />
      </div>

      {/* Editor */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#999]">Inhalt</label>
        <div className="mt-1 flex items-center gap-2 mb-2">
          <button type="button" data-testid="newsletter-ai-generate" onClick={() => runAi("generate")} disabled={!!aiBusy}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-[11px] font-semibold bg-[#0A0A0A] text-white hover:bg-[#222] disabled:opacity-40">
            {aiBusy === "generate" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Mit KI erstellen
          </button>
          <button type="button" data-testid="newsletter-ai-improve" onClick={() => runAi("improve")} disabled={!!aiBusy}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-[11px] font-semibold bg-[#F5F5F5] text-[#0A0A0A] hover:bg-[#E5E5E5] disabled:opacity-40">
            {aiBusy === "improve" ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Optimieren
          </button>
        </div>
        <RichTextEditor
          value={bodyHtml}
          onChange={setBodyHtml}
          placeholder="Schreibe deinen Newsletter… Fett, Überschriften, Listen, Emojis über die Toolbar."
          minHeight="260px"
          testId="newsletter-editor"
        />
        {aiError && <p data-testid="newsletter-ai-error" className="text-xs text-red-600 mt-1">{aiError}</p>}
      </div>

      {/* Aktionen */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="newsletter-test-btn"
          onClick={sendTest}
          disabled={testBusy}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F5F5] disabled:opacity-50"
        >
          {testBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Test an mich senden
        </button>
        <button
          data-testid="newsletter-send-btn"
          onClick={() => { setSendError(""); setShowConfirm(true); }}
          disabled={!canSend}
          className={`inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-sm font-medium ${canSend ? "bg-[#0A0A0A] text-white hover:bg-[#222]" : "bg-[#F0F0F0] text-[#999] cursor-not-allowed"}`}
        >
          <Send size={14} /> Senden ({count ?? 0})
        </button>
        {testResult && <span data-testid="newsletter-test-result" className="text-xs text-[#666]">{testResult}</span>}
      </div>

      {/* Verlauf */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-[#999] mb-2">Letzte Kampagnen</p>
        <div className="bg-white border border-[#E5E5E5] rounded overflow-hidden">
          {campaigns.length === 0 ? (
            <p className="px-3 py-4 text-sm text-[#999]">Noch keine Newsletter versendet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#999]">
                <tr>
                  <th className="text-left px-3 py-2">Betreff</th>
                  <th className="text-left px-3 py-2 w-40">Kreise</th>
                  <th className="text-left px-3 py-2 w-24">Versendet</th>
                  <th className="text-left px-3 py-2 w-24">Status</th>
                  <th className="text-left px-3 py-2 w-36">Datum</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-[#F0F0F0]" data-testid={`newsletter-campaign-${c.campaign_id}`}>
                    <td className="px-3 py-2 text-[#0A0A0A] max-w-[220px] truncate">{c.subject}</td>
                    <td className="px-3 py-2 text-[11px] text-[#666]">{(c.segments || []).join(", ")}</td>
                    <td className="px-3 py-2 text-[#333]">{c.sent}/{c.recipients_total}{c.failed ? ` (${c.failed} Fehler)` : ""}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${c.status === "DONE" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>
                        {c.status === "DONE" ? <CheckCircle2 size={10} /> : <Loader2 size={10} className="animate-spin" />}
                        {c.status === "DONE" ? "Fertig" : "Sendet…"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#666]">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {campaigns.some((c) => c.status !== "DONE") && (
          <button onClick={loadCampaigns} className="mt-2 text-xs text-[#0A0A0A] hover:underline" data-testid="newsletter-refresh">Status aktualisieren</button>
        )}
      </div>

      {/* Bestätigungs-Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div data-testid="newsletter-confirm-modal" className="bg-white rounded-lg max-w-md w-full shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="font-semibold text-[#0A0A0A]">Newsletter senden</h3>
              <button onClick={() => setShowConfirm(false)} className="text-[#666] hover:text-[#0A0A0A]"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#333]">
                Du sendest <strong>„{subject}"</strong> an <strong>{count ?? 0}</strong> Empfänger der Kreise <strong>{Array.from(segments).join(", ")}</strong>.
              </p>
              <p className="text-xs text-[#666]">Der Versand läuft im Hintergrund. Fortschritt siehst du im Kampagnen-Verlauf.</p>
              {sendError && <div className="text-xs text-red-600">{sendError}</div>}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setShowConfirm(false)} className="px-4 py-2 text-sm text-[#666] hover:text-[#0A0A0A]">Abbrechen</button>
                <button
                  data-testid="newsletter-confirm-send"
                  onClick={doSend}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#222] text-white rounded disabled:opacity-50"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Jetzt senden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
